import React, { memo } from 'react';

/**
 * A single tier band within a tiered container, in the container's interior
 * coordinate system (same coordinates as the laid-out child node positions).
 * Bands are contiguous and tile the full interior height; `depth` is the
 * 0-based tier index from the top.
 */
export interface TierBand {
  top: number;
  height: number;
  depth: number;
  /**
   * Y for the tier's corner number (interior coords) — the top of the tier's
   * band, clamped below the cluster header for the first tier so it lands in
   * the true top-left corner of the tier, not down at the node row.
   */
  numberTop: number;
}

// Node-spacing knobs — these position the node rows (via ELK), and thus where
// the tier boundaries fall. TIER_EDGE_GAP reserves room above the first row /
// below the last; TIER_ROW_GAP between rows; TIER_BAND_PAD a little extra
// breathing room around each row.
export const TIER_BAND_PAD = 4;
export const TIER_EDGE_GAP = 3;
export const TIER_ROW_GAP = 14;

// Tier corner number — small ordinal in the top-left of each tier, shown only
// when a cluster is actually tiered (>1 tier).
export const TIER_NUMBER_INSET = 6;

/**
 * Tiers are shown as alternating row shading (zebra), not colored rails:
 * odd-depth tiers get a faint neutral wash, even tiers stay on the cluster
 * surface (white). White-first means a single-tier cluster shows no shading at
 * all (nothing to differentiate), 2 tiers shade the lower row, 3 the middle,
 * etc. — the standard zebra convention, which also degrades cleanly by tier
 * count.
 */
export function isShadedTier(depth: number): boolean {
  return depth % 2 === 1;
}

export interface TierLanesProps {
  bands: TierBand[];
}

/**
 * Renders intra-cluster dependency tiers as alternating full-row background
 * bands behind the node cards. Odd tiers get a faint, TRANSLUCENT
 * `--surface-sunken` wash (a recessed "lane"); even tiers are left on the
 * cluster surface. The translucency matters: edges render BELOW the region
 * (see GRAPH_LAYER in clusterLayout), so an opaque band would erase any
 * connector passing behind a shaded tier — the wash must let edges show
 * through. Purely decorative (aria-hidden, pointer-events: none); the parent
 * must be `position: relative`.
 */
function TierLanes({ bands }: TierLanesProps) {
  if (!bands.length) {
    return null;
  }
  // Tier corner numbers only when the cluster is actually tiered (>1 tier).
  const showNumbers = bands.length > 1;
  return (
    <>
      {bands.filter((band) => isShadedTier(band.depth)).map((band) => (
        <div
          key={`band-${band.depth}`}
          aria-hidden
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: band.top,
            height: band.height,
            // Translucent so edges (which sit below the region) stay visible
            // where they pass behind a shaded tier. Tune the % visually.
            background: 'color-mix(in srgb, var(--surface-sunken) 70%, transparent)',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        />
      ))}
      {showNumbers && bands.map((band) => (
        <div
          key={`num-${band.depth}`}
          aria-hidden
          style={{
            position: 'absolute',
            top: band.numberTop + TIER_NUMBER_INSET,
            left: TIER_NUMBER_INSET,
            fontSize: 10,
            fontWeight: 600,
            lineHeight: 1,
            color: 'var(--fg-tertiary)',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          {band.depth + 1}
        </div>
      ))}
    </>
  );
}

export default memo(TierLanes);
